/*
 * options properties:
 *   - columns: slick grid column definitions
 *   - target: selector to place grid into
 *   - editable: enable cell editing
 *   - updatePath: given an id, returns a url to update an item
 *   - updateParam: the param name to place attributes under for updating (route/event/etc)
 *   - viewPath: given an item, returns a url to view item
 *   - ignoreCaseOnSort: when sorting by this column, sort case insensitive
 */
export class FilteringGridWithBulkOperations {
  constructor(data, {columns, target, editable=false, updatePath, updateParam, viewPath, viewFormatter, editPath, ignoreCaseOnSort=[], filterAccessors, settingsOpts, stickySortFn}) {
    let cbSelector;
    this._requestErrorListeners = []
    this._updateDoneListeners = {}
    this._noFilterQueryParams = {}

    // if grid is editable, add a checkbox column to allow bulk operations
    if(editable) {
      cbSelector = new Slick.CheckboxSelectColumn({ cssClass: 'slick-cell-checkboxsel' });
      columns.unshift(cbSelector.getColumnDefinition());
    }
    if (viewPath || viewFormatter) {
      const defaultFormatter = (row,cell,val,col,item) => (`<a class="row-link" href="${viewPath(item)}">View</a>`)
      columns.push({
        id:"view", name: 'View', field:"view", width: 63, resizable: false,
        formatter: viewFormatter ? viewFormatter : defaultFormatter
      });
    }
    if (editable && editPath) {
      columns.push({
        id:"edit", name: 'Edit', field:"edit", width: 63, resizable: false,
        formatter: (row,cell,val,col,item) =>  '<a class="row-link" href="' + editPath(item) + '">Edit</a>'
      });
    }

    // create the slickgrid data view, and the grid
    const dataView = this.dataView = new Slick.Data.DataView();
    const autoHeight = data.length < 15
    const slickGridOptions = {
      enableCellNavigation: true,
      enableColumnReorder: false,
      editable: editable,
      asyncEditorLoading: false,
      autoEdit: false,
      forceFitColumns: true,
      autoHeight
    };
    if (autoHeight) {
      $(target).css({height: 'auto'})
      $('.modify_grid_height').hide()
    }
    if (editable) {
      columns.forEach(col => {
        if (col.editor) {
          col.cssClass = 'can-modify'
        }
      })
    }
    const slickGrid = this.slickGrid = new Slick.Grid(target, this.dataView, columns, slickGridOptions);

    // we want clicks to select a row (the other option is a single cell)
    slickGrid.setSelectionModel(new Slick.RowSelectionModel({selectActiveRow: false}));

    // registering the checkbox column ties it into row selection (visually & keeping track of what's selected)
    if(editable) {
      slickGrid.registerPlugin(cbSelector);
    }

    // allows mouseover of truncated cells to display full cell contents
    slickGrid.registerPlugin(new Slick.AutoTooltips());

    // grid -> dataview bindings
    slickGrid.onCellChange.subscribe(function(e,args) {
      const attrs = {}, params = {_method: 'put'},
        field = args.grid.getColumns()[args.cell].field;
      dataView.updateItem(args.item.id, args.item);
      attrs[field] = args.item[field];
      params[updateParam] = attrs;
      $.post(updatePath(args.item.id), params, function() {});
    });

    // dataview -> grid bindings
    dataView.onRowCountChanged.subscribe(function() {
      slickGrid.updateRowCount();
      slickGrid.render();
    });
    dataView.onRowsChanged.subscribe(function(e,args) {
      slickGrid.invalidateRows(args.rows);
      slickGrid.render();
    });

    // sorting - set up case insensitive sort columns, and bind grid sort action to dataview
    for (let col of ignoreCaseOnSort) {
      this.sortingFn(col, caseInsensitiveStringSortFn(col));
    }
    slickGrid.onSort.subscribe((e,args) => {
      slickGrid.setSelectedRows([])
      if (_.isFunction(stickySortFn)) {
        dataView.sort(stickySortCompose(stickySortFn, this.sortingFn(args.sortCol.field), args.sortAsc), args.sortAsc)
      } else {
        dataView.sort(this.sortingFn(args.sortCol.field), args.sortAsc)
      }
    });

    // filterAccessors should be a Map of names to accessor methods. Each
    // method should return the current UI value of the filter when called
    // without arguments, and set the UI value of the filter to the first
    // argument when called with one or more arguments
    this.filterAccessors = filterAccessors

    dataView.beginUpdate();
    dataView.setItems(data);
    dataView.endUpdate();

    // send settings data to the server when club members change table settings
    // settingsOpts should be an object with the following keys:
    // - url: The URL to send the data to
    // - name: The name of this table
    // - method (optional): HTTP method to send data with
    if (settingsOpts) {
      this.settingsOpts = settingsOpts

      slickGrid.onColumnsResized.subscribe((/*event, args*/) => {
        const changes = slickGrid.getColumns()
          .reduce((result, column) => { result[column.field] = column.width; return result }, {})

        if (Object.keys(changes).length > 0) { this.sendSettings({ columnWidths: changes }) }
      })

      slickGrid.onSort.subscribe((event, args) => {
        this.sendSettings({ sortAsc: args.sortAsc, sortCol: args.sortCol.field })
      })
    }

    this._stickySortFn = stickySortFn

    // Disable editing properties of items that are locked
    slickGrid.onBeforeEditCell.subscribe((e, args) => {
      if (args.item.locked) {
        return false
      }
    })
  }

  // setter/getter for sorting functions by column name.
  // uses comparison operators against column field by default
  sortingFn(col, fn) {
    if(!this._sortFnCache) this._sortFnCache = {};

    if(!this._sortFnCache[col]) {
      this._sortFnCache[col] = fn || basicSortFn(col);
    }
    return this._sortFnCache[col];
  }

  setSort(column, asc) {
    if (_.isFunction(this._stickySortFn)) {
      this.dataView.sort(stickySortCompose(this._stickySortFn, this.sortingFn(column), asc), asc)
    } else {
      this.dataView.sort(this.sortingFn(column), asc)
    }

    this.slickGrid.setSortColumn(column, asc)
  }

  /*
   * Bulk operations
   */

  enableBulkOperations({bulkUpdateAttributePath, bulkDeletePath, idsParam}) {
    this._bulkUpdateAttributePath = bulkUpdateAttributePath;
    this._bulkDeletePath = bulkDeletePath;
    this._bulkUpdateIdsParam = idsParam;
  }

  // update on server, set attr=value in dataview, update grid
  // optional updateFn will get called with item and can modify additional fields on item during update
  setAttributeOnSelectedItems(attr, value, updateFn) {
    const self = this
    return this.remoteRequest({
      url: this._bulkUpdateAttributePath,
      params: (selectedIds) => { return { attr, value, [this._bulkUpdateIdsParam]: selectedIds.join(',') } },
      done: (dataView) => {
        // TODO: Read values from result data. Will require a mapper to get an
        // array of items from various json structures
        dataView.beginUpdate()
        self.selectedItems.forEach(item => {
          item[attr] = value
          if (updateFn) { updateFn(item) }
          dataView.updateItem(item.id, item)
        })
        dataView.endUpdate()
      }
    })
  }

  deleteSelectedItems() {
    const self = this
    const selectedIds = this.selectedItems.map(({id}) => id)

    this.remoteRequest({
      url: this._bulkDeletePath,
      params: (selectedIds) => { return { [this._bulkUpdateIdsParam]: selectedIds.join(',') } },
      done: (dataView) => {
        // TODO: Read deleted items from returned data
        dataView.beginUpdate()
        selectedIds.forEach(id => dataView.deleteItem(id))
        dataView.endUpdate()
        self.slickGrid.setSelectedRows([]);
      }
    })
  }

  // opts must be an object with the following keys:
  // - url: endpoint of the remote url
  // optional keys:
  // - params: params to send to the remote endpoint. if params is a function,
  //   it will be called with an array of selected ids and the return value will
  //   be used as the params for the remote request
  // - done: a function that takes two parameters, dataView and data, with data
  //   being the response from the remote endpoint
  // - fail: called with a list of errors if the request fails
  // - always: always called when request has completed successfully or not
  // - skipFailListeners: if true, does not notify onRequestError listeners on
  //   request failure
  remoteRequest(opts) {
    const self = this
    const ids = this.selectedItems.map(({id}) => id)
    const params = (typeof(opts.params) === "function") ? opts.params(ids) : (opts.params || {})
    const done = opts.done || function() {}

    return $.post(opts.url, params)
      .done(data => done(self.dataView, data))
      .fail(xhr => { self._requestFail(xhr, opts.fail, opts.skipFailListeners) })
      .always(() => { opts.always && opts.always() })
  }

  // Adds a handler that gets called when any remote request fails
  onRequestError(handler) {
    this._requestErrorListeners.push(handler)
  }

  // Internal helper that alerts listeners to failed remote requests
  _requestFail(xhr, extraCallback, skipListeners) {
    var errors = []
    const data = xhr.responseJSON
    if (data) {
      if (data.errors) {
        errors = data.errors
      } else if (data.error) {
        errors = [data.error]
      }
    }

    if (!skipListeners) {
      this._requestErrorListeners.forEach(listener => listener(errors))
    }

    if (extraCallback) { extraCallback(errors) }
  }

  // returns all checked off items when used in editable mode (where checkboxes are present)
  get selectedItems() {
    return this.slickGrid.getSelectedRows().map(i => this.dataView.getItem(i));
  }

  setSelectedRows(rows) {
    return this.slickGrid.setSelectedRows(rows)
  }

  /*
   * Filtering
   *
   * Example:
   *
   * let grid = new FilteringGridWithBulkOperations(data, opts);
   * grid.enableFiltering([ {field: 'name', type: 'string'} ]);
   * grid.filter('name', 'Foo'); //-> only shows items with Foo in name
   *
   */

  // filterSpec should look like this: [ {field: 'fieldname', type: 'string'}, ...]
  // once called, use filter(field, value) to perform filtering
  //
  // supported types:
  //   - string: case-insensitive substring matching
  //   - array: includes check, use special value "_empty" to match empty arrays
  //   - range: numeric range filtering by calling filter(field, low, high)
  //   - anyString: string OR match against array of fields in 'extra'
  //   - function: 'extra' is a function that tests item, expects bool-like return
  enableFiltering(filterSpec) {
    this._filterSpec = filterSpec;

    // initialize all filter keys to []
    this._filterValues = filterSpec.reduce((acc, {field}) => { acc[field] = []; return acc; }, {});
    this.dataView.setFilter(this._filterIncludesItem.bind(this));
    this._applyFiltersFromUrl();
  }

  // set a filter, and update grid to only display items that pass filters
  filter(field, ...rest) {
    // assumes a string for now with the toLowerCase call
    this._filterValues[field] = rest;
    this._refreshFilters();
    (this._filterChangeListeners || []).forEach(listener => listener({[field]: rest}))
  }

  // registers a callback to be called when a filter changes. The callback
  // should take a single argument, which is an object with field names for
  // keys and filter values for values
  onFilterChange(func) {
    if (!this._filterChangeListeners) { this._filterChangeListeners = [] }
    this._filterChangeListeners.push(func)
  }

  // returns with current filters as an object with field name keys and filter
  // value values
  getFilters() {
    return this._filterValues
  }

  // applies filters found in the location hash
  _applyFiltersFromUrl() {
    let persistParams = rwgps.Utils.getQueryParameters()
    let urlFilters = {}
    for (let [key, value] of Object.entries(persistParams)) {
      value = decodeURIComponent(value);
      let specs = this._filterSpec.filter(spec => spec.field === key)
      if (specs.length) {
        let filterSpec = specs[0]
        let parsedFilter, tmp
        switch(filterSpec.type) {
          case 'range':
            parsedFilter = [0, Infinity]
            tmp = value.split(',')
            if (jQuery.isNumeric(tmp[0])) parsedFilter[0] = parseInt(tmp[0])
            if (tmp[1] && jQuery.isNumeric(tmp[1])) parsedFilter[1] = parseInt(tmp[1])
            break
          case 'arraySubset':
            parsedFilter = value.split(',')
            break
          default:
            parsedFilter = value
            break
        }
        if (parsedFilter) {
          urlFilters[key] = parsedFilter
        }
      } else if (key.length) {
        // Keep track of query param that is not a filter (e.g. ?archive=true)
        this._noFilterQueryParams[key] = value
      }
    }
    this.setFilters(urlFilters)
  }

  // updates location hash with current filters
  _persistFiltersToUrl(filters) {
    let persistFilters = {}
    for (let [key, value] of Object.entries(filters)) {
      let filterSpec = this._filterSpec.filter(spec => spec.field === key)[0]
      let persistValue
      switch (filterSpec.type) {
        case 'range':
          if (value[0] !== 0 || value[1] !== Infinity) {
            persistValue = value[1] === Infinity ? [value[0]] : value
          }
          break
        default:
          if (value.length) persistValue = value
          break
      }
      if (persistValue) persistFilters[key] = persistValue
    }
    let allParams = Object.assign({}, persistFilters, this._noFilterQueryParams);
    let queryString = Object.keys(allParams)
      .map(k => `${k}=${encodeURIComponent(allParams[k].toString())}`)
      .join('&')
    window.history.replaceState({}, document.title, `?${queryString}`)
  }

  // sets the current filters in bulk, refreshes the data view, and sets the
  // filter inputs in the UI
  setFilters(filters, triggerListeners = true, setInputs = true) {
    filters = Object.assign(filters) // Grab a copy so we can modify without worry

    if (setInputs) {
      this.filterAccessors.forEach((accessor, field) => accessor(filters[field]))
    }

    Object.keys(this._filterValues).forEach(key => {
      var newValue = filters[key] || null
      if (newValue === null) { newValue = [] } // Rails munges empty arrays to nil, so convert them back
      if (!Array.isArray(newValue)) { newValue = [newValue] }
      this._filterValues[key] = newValue
    })

    this._refreshFilters()

    if (triggerListeners) {
      (this._filterChangeListeners || []).forEach(listener => listener(filters))
    }
    this._persistFiltersToUrl(filters)
  }

  // Call this function when filter inputs have changed and should be re-read
  filterInputsChanged() {
    var filters = {}
    this.filterAccessors.forEach((accessor, field) => filters[field] = accessor())
    this.setFilters(filters, true, false)
  }

  sendSettings(data) {
    const opts = this.settingsOpts
    $.ajax({
      url: opts.url,
      method: opts.method || "PUT",
      data: JSON.stringify({ table_settings: { [opts.name]: data } }),
      contentType: "application/json"
    })
  }

  // internal helper
  _refreshFilters() {
    this.slickGrid.setSelectedRows([])
    this.dataView.setFilterArgs(this._filterValues)
    this.dataView.refresh()
  }

  // internal helper to check if an item should be displayed or hidden based on the current filter criteria
  _filterIncludesItem(item, args) {
    // no filter args on initial data load - show everything
    if(!args) return true;

    let testVal;
    for(let {field, type, extra} of this._filterSpec) {
      let doesMatch = false
      switch(type) {
        case 'string':
          testVal = (args[field][0] || '').toLowerCase();
          if(! (item[field]||'').toLowerCase().includes(testVal))
            return false;
          break;
        case 'array':
          testVal = args[field][0];
          if(! (!testVal || (item[field].includes(testVal) || (testVal == '_empty' && !item[field].length))) )
            return false;
          break;
        case 'arraySubset':
          // The item will be filtered out unless testVal (an array of strings)
          // is a subset of item[field] (an array of strings)
          testVal = args[field]
          if(! (!testVal || testVal.every((str) => item[field].indexOf(str) >= 0)) )
            return false;
          break;
        case 'range':
          if(item[field] < args[field][0] || item[field] > args[field][1])
            return false;
          break;
        case 'anyString':
          for (let subField of extra) {
            testVal = (args[field][0] || '').toLowerCase();
            if ((item[subField]||'').toLowerCase().includes(testVal))
              doesMatch = true
          }
          if (!doesMatch)
            return false
          break;
        case 'function':
          if (!extra(item, args[field][0]))
            return false
      }
    }
    return true;
  }
}

FilteringGridWithBulkOperations.formatters = {
  visibility: function(row, cell, value) {
    if (value == 0) {
      return 'Public';
    } else if (value == 1) {
      return 'Managers';
    } else if (value == 2) {
      return 'Members';
    }
  },
  // must be bound to view so it can check this.userDateFormat
  date: function(row, cell, value) {
    if(value == null) return
    if(typeof value === "string") {
      value = value.indexOf('T') === -1 ? rwgps.DateExt.parseDate(value) : rwgps.DateExt.parse(value)
    }

    if (this.userDateFormat == "ymd") {
      return value.strftime("%y/%m/%d");
    } else if (this.userDateFormat == "dmy") {
      return value.strftime("%d/%m/%y");
    } else {
      return value.strftime("%m/%d/%y");
    }
  },
  tags: (row,cell,value,col,item) => {
    return (item.tag_names || []).join(', ');
  },
  genericArray: function (row, cell, value) {
    return (value || []).join(', ')
  },
  distance: (row,cell,value) => {
    return rwgps.LabelMaker.longLabel(value);
  },
  elevationGain: (row,cell,value) => {
    return rwgps.LabelMaker.shortLabel(value);
  },
  checkbox: (row,cell,value) => {
    return `<input type="checkbox" ${value && 'checked'} class="grid_checkbox">`
  },
  boolToYN: (row, cell, value) => {
    if (value) {
      return "Yes"
    } else {
      return "No"
    }
  },
  capitalize: (row, cell, value) => {
    return value[0].toUpperCase() + value.slice(1)
  },
  info: (row,cell,value,col,item) => {
    if (item.locked) {
      return `<a href="#" class="route-info" data-toggle="modal" data-id=${item.id} style="color:#f80;"><i style="font-size:16px" class="icon-lock"></i></a>`
    } else {
      return `<a href="#" class="route-info" data-toggle="modal" data-id=${item.id} style="color:#f80;"><span style="font-size:16px" class="icon-info-sign"></span></a>`
    }
  },
  routeLink: (row,cell,value,col,item) => {
    const color = rwgps.hasRouteLink[item.id] ? '#f80' : '#ccc';
    return `<a href="#" class="route-link" data-toggle="modal" data-id=${item.id} style="color:${color};"><span style="font-size:16px" class="icon-link"></span></a>`
  },
  // must be bound to view so it can check this.userPinnedRoutes
  pin: function(row,cell,value,col,item) {
    var userPinnedRoutes = this.userPinnedRoutes || []
    let match = 0;
    for (var i=0; i < userPinnedRoutes.length; i++) {
      if (item.id == userPinnedRoutes[i].object_id) {
        match += 1
      }
    }
    if (match > 0) {
      return ""
    } else {
      return '<a class="remember_this" style="color:#f80;" href="/remember/route/'+item.id+'"><span class="icon icon-pin" style="font-size:20px"></span></a>';
    }
  },
  // must be bound to view so it can check this.editable
  routeName: function(row,cell,value,col,item) {
    const text = _.escape(value)

    var previewAttributes = `class="showPreview" data-id="${item.id}" data-type="route" data-name="${item.name}" data-elevationGain="${item.elevation_gain}" data-elevationLoss="${item.elevation_loss}" data-distance="${item.distance}" data-privacyCode="${item.privacyCode}"`

    if (this.editable) {
      let result = ''
      if (item.locked) {
        result += '<i class="icon-lock icon-large"></i> '
      }
      return `<span ${previewAttributes}>${result}${text}</span>`
    } else {
      let url = "/routes/" + item.id;
      if (item.privacy_code_string) {
        url += "?privacy_code=" + item.privacy_code_string
      }
      return `<a href="${url}" ${previewAttributes}>${text}</a>`
    }
  },
  // must be bound to view so it can check this.editable
  eventName: function(row,cell,value,colDef,item) {
    let out = "";
    if(!this.editable) {
      const url = "/events/" + item.slug;
      out += '<a href="' + url + '">';
    }
    out += _.escape(value);
    if(!this.editable) {
      out += '</a>';
    }
    return out;
  }
};

/*
 * utilities to generate sorting functions
 */

function caseInsensitiveStringSortFn(col) {
  return function(a,b) {
    const x = (a[col] || '').toUpperCase(), y = (b[col] || '').toUpperCase();
    return (x==y ? 0 : (x > y ? 1 : -1));
  }
}

function basicSortFn(col) {
  return function(a, b) {
    const x = a[col], y = b[col];

    // Handle null/undefined values
    if (x == null && y == null) return 0;
    if (x == null) return -1;
    if (y == null) return 1;

    return (x == y ? 0 : (x > y ? 1 : -1));
  }
}

function stickySortCompose(stickyFn, regularFn, sortAsc) {
  return function (a, b) {
    let stickyResult = stickyFn(a, b);
    if (stickyResult === 0) {
      return regularFn(a, b);
    }
    // No matter the sort direction, sticky is always first
    return (sortAsc ? -1 * stickyResult : stickyResult);
  }
}



// WEBPACK FOOTER //
// ./public/javascripts/clubs/filtering_grid_with_bulk_operations.js